import type { ComponentChildren } from 'preact';
import { CalendarRange } from 'lucide-preact';
import { cn } from '@/lib/cn';
import { formatDateRange, formatShortDate } from '@/lib/format';

type DateInput = string | Date | null | undefined;

export interface ViewPeriodProps {
  /** Rango que muestra la vista. Con `from === to` (o sin `to`) se pinta una sola fecha. */
  from?: DateInput;
  to?: DateInput;
  /** Texto libre cuando el periodo no es un rango de fechas ("últimos 12 meses"). */
  children?: ComponentChildren;
  /** Prefijo. Por defecto "Esta vista usa". */
  label?: string;
  className?: string;
}

/**
 * Periodo real de una vista, para el `meta` de `PageHeader`.
 *
 * Se usa en las pantallas cuyo periodo NO es el rango global del selector (una foto de
 * un día, la ventana de un informe, un histórico completo), para que no parezca que
 * obedecen al rango elegido en otra pantalla. Las fechas salen de `formatDateRange` /
 * `formatShortDate`, el mismo formato que el selector.
 */
export function ViewPeriod({
  from,
  to,
  children,
  label = 'Esta vista usa',
  className,
}: ViewPeriodProps) {
  const dates =
    children ??
    (from && to && from !== to ? formatDateRange(from, to) : formatShortDate(from ?? to));
  return (
    <span className={cn('text-fg-muted inline-flex items-center gap-1.5 text-xs', className)}>
      <CalendarRange size={14} aria-hidden="true" className="shrink-0" />
      <span>
        {label}: <span className="text-fg tabular-nums">{dates}</span>
      </span>
    </span>
  );
}
